import { useState } from 'react'
import styled from 'styled-components'
import { CustomProvider, DateRangePicker } from 'rsuite'
const { afterToday } = DateRangePicker
import ptBR from 'rsuite/locales/pt_BR'
import { Box, Checkbox, FormControlLabel, Tooltip } from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import {
  AutoCompleteWrapper,
  BarChartWrapper,
  ChartWithNoteWrapper,
  CustomChartLegend,
  Filter,
  FilterDrawer,
  GroupedFilterTags,
} from '../../components'
import useViewport from '../../hooks/useViewport'
import filterDrawerHelper from '../../utils/filterDrawer'
import { RequestService } from '../../services/RequestService'
import {
  ShiftFatigueProvider,
  useShiftFatigue,
} from '../../contexts/ShiftFatigueContext'
import { useCustomLegend } from '../../hooks'
import { useTranslation } from "react-i18next"

const Container = styled(Box)`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
`

const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #f1f3f5;
  padding: 24px 44px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ChartContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
`

const FiltersTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`

const ShiftFatigueWrapper = () => {
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const {
    data,
    loading,
    categories,
    selectedUser,
    onSelectUser,
    dateRange,
    onDateRangeChange,
    selectedFilterOptions,
    filterOptions,
    loadingFilters,
    onSelectFilter,
    onShift,
    onCheckOnShift,
    inputValue,
    onInputValueChange,
    graphMessage,
  } = useShiftFatigue()
  const { width } = useViewport()
  const { t } = useTranslation('common')
  const legends = [
    { label: t('reports.shift_fatigue.legends.bad.text'), color: '#fd564e' },
    { label: t('reports.shift_fatigue.legends.regular.text'), color: '#ffe000' },
    { label: t('reports.shift_fatigue.legends.good.text'), color: '#62E751' },
  ]

  const initalLegendState = {
    bad: true,
    regular: true,
    good: true,
  }
  const { activeSeries, visibleSeries, toggleSeries, defineMax } =
    useCustomLegend({
      initalLegendState,
      data,
    })
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper

  return (
    <Container>
      <Body>
        <Title>
          <span style={{ fontSize: 20, fontWeight: 600 }}>
            {t('reports.shift_fatigue.title')}
          </span>
          <Tooltip
            title={
              <div style={{ maxWidth: 300 }}>
                <p>
                  <strong>
                    {t('reports.shift_fatigue.tooltip.one')}
                  </strong>
                </p>
                <p>
                  {t('reports.shift_fatigue.tooltip.two')}
                </p>
                <br />
                <p>
                  {t('reports.shift_fatigue.tooltip.three')}
                </p>
                <br />
                <p>
                  {t('reports.shift_fatigue.tooltip.four')}
                </p>
              </div>
            }
            placement='right'
            arrow
          >
            <HelpOutline fontSize='24' />
          </Tooltip>
        </Title>
        <ChartContainer>
          <FiltersTagsContainer>
            <FiltersContainer>
              <div style={{ display: 'flex', flex: 1.5 }}>
                <AutoCompleteWrapper
                  onLoad={() => RequestService.getUsers({ visibility: 'reports' })}
                  value={selectedUser}
                  onChangeValue={onSelectUser}
                  inputValue={inputValue}
                  onInputValueChange={onInputValueChange}
                />
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'flex-end',
                    gap: 16,
                  }}
                >
                  <CustomProvider locale={ptBR}>
                    <DateRangePicker
                      defaultCalendarValue={dateRange}
                      value={dateRange}
                      onChange={onDateRangeChange}
                      showOneCalendar
                      shouldDisableDate={afterToday()}
                      character=' - '
                      placement='bottom'
                      placeholder={t('reports.shift_fatigue.placeholder')}
                      ranges={[]}
                      cleanable={false}
                    />
                  </CustomProvider>
                  <Filter onClick={() => setOpenFilterDrawer(true)} />
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={onShift === t('yes')}
                      onChange={() =>
                        onCheckOnShift(onShift === t('yes') ? t('no') : t('yes'))
                      }
                    />
                  }
                  label={t('reports.shift_fatigue.on_shift')}
                />
              </div>
            </FiltersContainer>
            {selectedFilterOptionsList({
              selectedFilterOptions,
            })?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <GroupedFilterTags
                  tags={selectedFilterOptionsList({
                    selectedFilterOptions,
                  })}
                  onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                  maxTagsToShow={getMaxTagsToShow({ width })}
                />
              </div>
            )}
          </FiltersTagsContainer>

          <ChartWithNoteWrapper
            totalHours={graphMessage?.totalHours}
            totalUsers={graphMessage?.totalUsers}
          >
            {!loading && (
              <BarChartWrapper
                data={activeSeries}
                legend={{ show: false }}
                categories={categories}
                colors={data.map(s => s.color)}
                yProps={{
                  ...defineMax(),
                }}
              />
            )}

            <CustomChartLegend
              visibleSeries={visibleSeries}
              toggleSeries={toggleSeries}
              legends={legends}
            />
          </ChartWithNoteWrapper>
        </ChartContainer>
      </Body>
      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={onSelectFilter}
        loading={loadingFilters}
        options={filterOptions}
        withShift
        withPosition
        withTeam
      />
    </Container>
  )
}

const ShiftFatigue = () => {
  return (
    <ShiftFatigueProvider>
      <ShiftFatigueWrapper />
    </ShiftFatigueProvider>
  )
}

export default ShiftFatigue
